import {
  Container,
  ContainerResolver,
  EntryDirection,
  Focus,
  Transitions,
} from './typings';
import { fromCoords, getFocusWithValidOffset, isLRUDKey } from './utils';
import { Keys } from '../utils/keys';

export type ResolverArgs = {
  keyCode: number;
  currentFocus: Focus;
  getContainer: ContainerResolver;
  currentContainer: Container | undefined;
  transitions: Transitions;
  entryDirection?: EntryDirection;
};

export const resolve = (args: ResolverArgs) => {
  const {
    currentContainer,
    currentFocus,
    entryDirection,
    keyCode,
    transitions,
  } = args;

  if (!isLRUDKey(keyCode)) {
    throw new Error('Unhandled keycode passed to resolve');
  }

  if (entryDirection) {
    // override the default entry-focus and/or offset through custom transitions

    const newFocus = { ...currentFocus, offset: 0 };

    switch (entryDirection) {
      case 'NEXT':
        newFocus.column = currentFocus.column + 1;
        break;
      case 'PREV':
        newFocus.column = currentFocus.column - 1;
        break;
      case 'ABOVE':
        newFocus.row = currentFocus.row - 1;
        break;
      case 'BELOW':
        newFocus.row = currentFocus.row + 1;
        break;
    }

    newFocus.id = `${newFocus.row}/${newFocus.column}/${newFocus.offset}`;

    if (currentContainer?.content.length) {
      // currentContainer is assumed to have been updated in transitions before resolve call
      return newFocus;
    } else {
      // find next container in direction, stop after one
      const focusAfterSingleSkippedContainer = args.transitions[entryDirection](
        { ...args, currentFocus: newFocus }
      );

      return focusAfterSingleSkippedContainer || args.currentFocus;
    }
  }

  // make sure navigation works even when being out-of-bounds.
  const currentFocusSafe = currentContainer?.content
    ? getFocusWithValidOffset(currentFocus, currentContainer.content)
    : currentFocus;

  switch (keyCode) {
    case Keys.VK_LEFT:
      if (currentContainer?.content.length) {
        if (
          currentContainer.constraint.width &&
          currentFocusSafe.offset % currentContainer.constraint.width === 0
        ) {
          return transitions.PREV(args);
        }

        const newFocus = fromCoords({
          ...currentFocusSafe,
          offset: currentFocusSafe.offset - 1,
        });

        if (newFocus.offset < 0) {
          return transitions.PREV(args);
        }
        return newFocus;
      }

      return transitions.PREV(args);

    case Keys.VK_RIGHT:
      if (currentContainer?.content.length) {
        if (currentContainer.constraint.width) {
          if (
            (currentFocusSafe.offset !== 0 &&
              Number.isInteger(
                (currentFocusSafe.offset + 1) /
                  currentContainer.constraint.width
              )) || // last element in row case
            currentContainer.constraint.width - 1 === 0 // single column case
          )
            return transitions.NEXT(args);
        }
        if (currentFocusSafe.offset + 1 >= currentContainer.content.length) {
          return transitions.NEXT(args);
        }

        return fromCoords({
          ...currentFocusSafe,
          offset: currentFocusSafe.offset + 1,
        });
      }

      return transitions.NEXT(args);

    case Keys.VK_UP:
      if (currentContainer?.content.length) {
        if (!currentContainer.constraint.width) {
          return transitions.ABOVE(args);
        }

        const newOffset =
          currentFocusSafe.offset - currentContainer.constraint.width;

        if (newOffset < 0) {
          return transitions.ABOVE(args);
        }

        return fromCoords({ ...currentFocusSafe, offset: newOffset });
      }

      return transitions.ABOVE(args);

    case Keys.VK_DOWN:
      if (currentContainer?.content.length) {
        if (!currentContainer.constraint.width) {
          return transitions.BELOW(args);
        }

        let newOffset =
          currentFocusSafe.offset + currentContainer.constraint.width;

        if (
          Math.ceil((newOffset + 1) / currentContainer.constraint.width) >
          Math.ceil(
            currentContainer.content.length / currentContainer.constraint.width
          )
        ) {
          // new row is below last content row
          return transitions.BELOW(args);
        } else {
          if (currentContainer.content.length - newOffset <= 0) {
            // since we were moving from the second to last row and the new offset went past the last card, set to the last card
            newOffset = currentContainer.content.length - 1;
          }
        }

        return fromCoords({ ...currentFocusSafe, offset: newOffset });
      }

      return transitions.BELOW(args);

    default:
      throw new Error('WTF');
  }
};
